import { useState, useMemo } from 'react';
import { DropdownItem } from '../components/SearchableDropdown/SearchableDropdown';
import IntentSlot from '../types/BotBuilder/IntentSlot';
import { SystemVariableDto } from '../services/SystemVariableService/SystemVariableService';
import Slot from '../types/BotBuilder/Slot';

interface UseVariableDropdownLogicProps {
  intentSlots?: IntentSlot[];
  globalSlots?: Slot[];
  systemVariables: SystemVariableDto[];
  onSelect: (varName: string, isSystem: boolean, isGlobal?: boolean) => void;
}

export default function useVariableDropdownLogic({
  intentSlots = [],
  globalSlots = [],
  systemVariables,
  onSelect
}: UseVariableDropdownLogicProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const allItems = useMemo(() => {
    const items: DropdownItem[] = [];
    const addedSlotNames = new Set<string>();

    intentSlots.forEach((slot) => {
      items.push({
        id: `slot-${slot.slotName}`,
        label: slot.slotName,
        sublabel: 'Question variable'
      });
      addedSlotNames.add(slot.slotName);
    });

    globalSlots.forEach((slot) => {
      if (!addedSlotNames.has(slot.slotName)) {
        items.push({
          id: `global-${slot.slotName}`,
          label: slot.slotName,
          sublabel: 'Global variable'
        });
      }
    });

    systemVariables.forEach((variable) => {
      items.push({
        id: `system-${variable.name}`,
        label: variable.name,
        sublabel: variable.description || 'System variable'
      });
    });

    return items;
  }, [intentSlots, globalSlots, systemVariables]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return allItems;
    }

    const query = searchQuery.toLowerCase().trim();
    return allItems.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.sublabel?.toLowerCase().includes(query)
    );
  }, [allItems, searchQuery]);

  const handleSearch = (query: string): void => {
    setSearchQuery(query);
  };

  const handleSelect = (item: DropdownItem): void => {
    const idStr = item.id.toString();
    const isSystem = idStr.startsWith('system-');
    const isGlobal = idStr.startsWith('global-');

    let varName: string;
    if (isSystem) {
      varName = idStr.replace('system-', '');
    } else if (isGlobal) {
      varName = idStr.replace('global-', '');
    } else {
      varName = idStr.replace('slot-', '');
    }

    onSelect(varName, isSystem, isGlobal);
  };

  return {
    filteredItems,
    handleSearch,
    handleSelect
  };
}
